// Finanças (contas, categorias, lançamentos, importação de extratos, cartões de crédito, recorrências,
// orçamento e relatórios) — ver docs/API.md do planbackgo, seções "Finanças — convenções", "Contas",
// "Categorias", "Lançamentos", "Importação de extratos", "Cartões de crédito", "Recorrências financeiras",
// "Importação de fatura", "Orçamento por categoria", "Relatórios" e "PDF de fatura".
//
// Convenções do contrato:
//  - dinheiro em CENTAVOS (`*_cents`, inteiro) com SINAL: negativo = saída/despesa, positivo = entrada/receita;
//  - realizado × previsto é pela data: `transaction_date <= hoje` realizado; depois, `is_projected: true`;
//  - saldo nunca é persistido (snapshot `AccountBalance` + soma dos lançamentos);
//  - datas de calendário "AAAA-MM-DD"; carimbos "AAAA-MM-DDTHH:MM:SS" (Brasília, sem fuso).

// ---------------------------------------------------------------- contas

export type AccountType = 'checking' | 'savings' | 'investment' | 'cash';

export interface AccountInput {
  name: string;
  account_type: AccountType;
  bank_code?: string | null;
  bank_name?: string | null;
  branch?: string | null;
  number?: string | null;
  /** Nome da paleta (utils/colors.ts). */
  color?: string | null;
  is_active?: boolean;
}

export interface Account extends AccountInput {
  id: number;
  /** Saldo até hoje (realizado). */
  balance_cents: number;
  /** Saldo incluindo os lançamentos futuros (previsto). */
  projected_cents: number;
  created_at: string;
  updated_at?: string;
}

/** Snapshot de saldo ("neste dia a conta fechou com R$ X"); upsert por data. */
export interface AccountBalance {
  id: number;
  account_id: number;
  balance_date: string;
  amount_cents: number;
  notes?: string | null;
  created_at: string;
}

export interface AccountBalanceInput {
  balance_date: string;
  amount_cents: number;
  notes?: string | null;
}

export interface StatementLine {
  kind: 'transaction' | 'balance';
  transaction?: Transaction;
  balance?: AccountBalance;
  date: string;
  amount_cents: number;
  /** Saldo corrido depois desta linha. */
  balance_cents: number;
}

export interface Statement {
  account: Account;
  start: string;
  end: string;
  opening_balance_cents: number;
  /** Saldo em `end` (ou hoje, se `end` > hoje). */
  closing_balance_cents: number;
  /** Saldo em `end` incluindo os previstos. */
  projected_balance_cents: number;
  income_cents: number;
  expense_cents: number;
  lines: StatementLine[];
}

// ---------------------------------------------------------------- categorias

export type Movement = 'expense' | 'income' | 'transfer';

export interface CategoryInput {
  name: string;
  movement: Movement;
  parent_category_id?: number | null;
  /** Nome de ícone do lucide (texto livre, opcional). */
  icon?: string | null;
  color?: string | null;
  sort_order?: number | null;
  is_disabled?: boolean;
}

export interface Category extends CategoryInput {
  id: number;
  /** Categoria do seed (ex.: "Transferência entre contas"): só muda nome/cor/ícone/ordem; não exclui. */
  is_system: boolean;
  transaction_count: number;
  /** Fase 5: orçamento mensal padrão (centavos positivos), se houver. Só categorias de despesa. */
  default_budget_cents?: number | null;
  created_at: string;
  updated_at?: string;
}

export interface CategoryFilter {
  movement?: Movement | '';
  include_disabled?: boolean;
}

// ---------------------------------------------------------------- lançamentos

export interface TransactionInput {
  account_id: number;
  transaction_date: string;
  description: string;
  /** ≠ 0, com sinal (negativo = despesa). */
  amount_cents: number;
  category_id?: number | null;
  notes?: string | null;
  /** Fase 7: "precisa agendar no banco" — só despesa (400 em receita); ignorado em linha de recorrência. */
  requires_scheduling?: boolean;
  /** Fase 7: 0..30 (padrão 5) — dias antes do vencimento em que o aviso começa. */
  schedule_lead_days?: number;
}

/**
 * Fase 7 — status derivado do agendamento (nunca persistido): `none` não exige; `scheduled` agendada (ou
 * já realizada por extrato); `to_schedule` dentro da janela de aviso; `upcoming` fora da janela;
 * `missed` venceu sem agendar.
 */
export type ScheduleStatus = 'none' | 'to_schedule' | 'upcoming' | 'scheduled' | 'missed';

/** Filtro `schedule_status` de GET /transactions (`pending` = to_schedule + upcoming + missed). */
export type ScheduleStatusFilter = Exclude<ScheduleStatus, 'none'> | 'pending';

export interface Transaction {
  id: number;
  account_id: number;
  account_name: string;
  account_color?: string | null;
  transaction_date: string;
  description: string;
  amount_cents: number;
  category_id?: number | null;
  category_name?: string | null;
  category_color?: string | null;
  category_movement?: Movement | null;
  notes?: string | null;
  /** Parcelamento: id da 1ª parcela do grupo. */
  transaction_group?: number | null;
  repeat_index?: number | null;
  repeat_total?: number | null;
  /** Transferência: id da perna de origem (as duas pernas carregam o mesmo). */
  transfer_id?: number | null;
  transfer_account_id?: number | null;
  transfer_account_name?: string | null;
  credit_card_invoice_id?: number | null;
  recurring_transaction_id?: number | null;
  fit_id?: string | null;
  import_batch_id?: number | null;
  is_projected: boolean;
  /** Fase 7: despesa paga por agendamento no banco. */
  requires_scheduling?: boolean;
  schedule_lead_days?: number;
  /** Carimbo de quando foi marcada como agendada. */
  scheduled_at?: string | null;
  schedule_status?: ScheduleStatus;
  /** Conciliada com o extrato importado (o previsto virou realizado). */
  reconciled_at?: string | null;
  created_at: string;
  updated_at?: string;
  last_updated_by?: string;
}

export interface InstallmentsInput {
  account_id: number;
  first_date: string;
  description: string;
  /** Total com sinal; dividido em parcelas mensais (sobra de centavos na 1ª). */
  total_cents: number;
  /** 2..72 */
  installments: number;
  category_id?: number | null;
  notes?: string | null;
}

export interface TransferInput {
  from_account_id: number;
  to_account_id: number;
  transaction_date: string;
  /** > 0 */
  amount_cents: number;
  description?: string | null;
  notes?: string | null;
}

export interface TransferResult {
  from: Transaction;
  to: Transaction;
}

export type TransactionKind = 'expense' | 'income' | 'transfer' | 'all';
export type TransactionSortBy = 'date' | 'amount' | 'description';
export type DeleteScope = 'one' | 'future';

export interface TransactionFilter {
  account_id?: number;
  category_id?: number;
  uncategorized?: boolean;
  kind?: TransactionKind;
  start?: string;
  end?: string;
  q?: string;
  include_projected?: boolean;
  /** Fase 7: só lançamentos com `requires_scheduling`. */
  schedule_status?: ScheduleStatusFilter;
  page?: number;
  page_size?: number;
  sort_by?: TransactionSortBy;
  sort_direction?: 'asc' | 'desc';
}

export interface SummaryByCategory {
  category_id?: number | null;
  name: string;
  color?: string | null;
  movement: Movement;
  amount_cents: number;
  count: number;
}

export interface TransactionSummary {
  start: string;
  end: string;
  income_cents: number;
  expense_cents: number;
  net_cents: number;
  realized_income_cents: number;
  realized_expense_cents: number;
  uncategorized_count: number;
  /** Só despesas, ordenado pelo valor absoluto; `category_id` null = "Sem categoria". */
  by_category: SummaryByCategory[];
}

// ---------------------------------------------------------------- importação

/** `pdf`: fatura de cartão (Fase 5) ou extrato de conta exportado pelo app do Itaú. */
export type ImportFileFormat = 'ofx' | 'csv' | 'xlsx' | 'pdf';
export type ImportTargetType = 'account' | 'card';
export type ImportBatchStatus = 'previewing' | 'confirmed' | 'discarded';
export type ImportLineStatus = 'new' | 'existing' | 'ignored' | 'imported';

/** Fase 7: lançamento previsto que uma linha nova do extrato pode realizar (conciliar). */
export interface ReconcileCandidate {
  transaction_id: number;
  transaction_date: string;
  description: string;
  amount_cents: number;
  category_name?: string | null;
  recurring_transaction_id?: number | null;
  is_scheduled: boolean;
  /** Dias entre a linha do extrato e o previsto (com sinal). */
  days_diff: number;
  /** Diferença de valor em centavos (com sinal). */
  amount_diff_cents: number;
}

export interface ImportLine {
  id: number;
  line_no: number;
  entry_date: string;
  description: string;
  amount_cents: number;
  fit_id?: string | null;
  status: ImportLineStatus;
  matched_transaction_id?: number | null;
  suggested_category_id?: number | null;
  suggested_category_name?: string | null;
  chosen_category_id?: number | null;
  chosen_category_name?: string | null;
  installment_index?: number | null;
  installment_total?: number | null;
  /** Lote de cartão: fatura-alvo pela competência de `entry_date` (editável pelo PATCH). */
  target_invoice_id?: number | null;
  target_invoice_label?: string | null;
  /** Fase 7 (lote de conta): previsto escolhido para conciliar; ausente = entra como lançamento novo. */
  reconcile?: ReconcileCandidate | null;
  /** Até 3 candidatos, melhor primeiro (`[]` fora de conta). */
  reconcile_options?: ReconcileCandidate[];
  /** Aviso do backend sobre a linha (ex.: "fatura já paga"). */
  note?: string | null;
  raw_line?: string | null;
}

/** Faturas-alvo presentes nas linhas de um lote de cartão. */
export interface ImportBatchInvoice {
  id: number;
  label: string;
  status: InvoiceStatus;
}

export interface ImportBatch {
  id: number;
  target_type: ImportTargetType;
  target_id: number;
  target_name: string;
  file_name: string;
  file_format: ImportFileFormat;
  import_layout_id?: number | null;
  status: ImportBatchStatus;
  line_count: number;
  imported_count: number;
  new_count: number;
  existing_count: number;
  ignored_count: number;
  /** Fase 7: linhas `new` que vão realizar um lançamento previsto (conciliação). */
  reconcile_count?: number;
  period_start?: string | null;
  period_end?: string | null;
  confirmed_at?: string | null;
  created_at: string;
  lines?: ImportLine[];
  /** Só lote de cartão. */
  invoices?: ImportBatchInvoice[];
  /** PDF de fatura: vencimento lido do cabeçalho (informativo). */
  pdf_due_date?: string | null;
  /** PDF de fatura: competência lida do cabeçalho, AAAAMM (informativo). */
  pdf_competence?: number | null;
  /** PDF de extrato de conta (Itaú): data de emissão do extrato (persistida no lote). */
  statement_balance_date?: string | null;
  /** PDF de extrato de conta (Itaú): "saldo em conta" lido do extrato, em centavos (persistido no lote). */
  statement_balance_cents?: number | null;
  /** Resposta do confirm: o ajuste de saldo da conta foi gravado com o saldo do extrato. */
  balance_updated?: boolean;
}

export interface ImportLinePatch {
  chosen_category_id?: number | null;
  status?: 'new' | 'ignored';
  description?: string;
  /** Só lote de cartão: fatura do mesmo cartão, não paga. */
  target_invoice_id?: number;
  /** Só lote de conta: id de uma das `reconcile_options`, ou 0 = não conciliar. */
  reconcile_transaction_id?: number;
}

export interface ImportConfirmInput {
  /** Lote de cartão: linhas " i/N" geram também as parcelas i+1..N nas faturas seguintes (padrão true). */
  generate_future_installments?: boolean;
  /** Lote de conta vindo de PDF de extrato: grava o ajuste de saldo da conta com o saldo do extrato (padrão true). */
  update_balance?: boolean;
}

export interface ImportSample {
  file_format: ImportFileFormat;
  delimiter?: string | null;
  /** Primeiras 15 linhas, em colunas (OFX: [data, descrição, valor, fitid]). */
  rows: string[][];
}

export interface ImportFilter {
  status?: ImportBatchStatus | '';
  page?: number;
  page_size?: number;
}

/** Formato de data em notação Go (o contrato aceita estes quatro). */
export type ImportDateFormat = '02/01/2006' | '2006-01-02' | '02-01-2006' | '2/1/2006';

export interface ImportLayoutInput {
  name: string;
  target_type: ImportTargetType;
  file_format: 'csv' | 'xlsx';
  /** ";" (padrão), ",", "\t" ou "|". */
  delimiter?: string | null;
  has_header: boolean;
  skip_rows: number;
  /** Índices 0-based. */
  date_column: number;
  description_column: number;
  amount_column: number;
  /** Crédito em coluna separada (débito fica em `amount_column`). */
  credit_column?: number | null;
  date_format: ImportDateFormat;
  decimal_comma: boolean;
  invert_sign: boolean;
}

export interface ImportLayout extends ImportLayoutInput {
  id: number;
  created_at: string;
  updated_at?: string;
}

export type ImportRuleAppliesTo = 'both' | 'account' | 'card';

export interface ImportRuleInput {
  /** Substring, sem distinção de maiúsculas. */
  pattern: string;
  category_id: number;
  applies_to: ImportRuleAppliesTo;
  movement?: Movement | null;
  priority: number;
  is_active: boolean;
}

export interface ImportRule extends ImportRuleInput {
  id: number;
  category_name: string;
  category_color?: string | null;
  created_at: string;
  updated_at?: string;
}

// ---------------------------------------------------------------- cartões de crédito

export type CardBrand = 'visa' | 'mastercard' | 'elo' | 'amex' | 'hipercard' | 'outro';

/** Derivado pelo backend (nunca persistido): paid > open (< fechamento) > closed > due (hoje) > overdue. */
export type InvoiceStatus = 'open' | 'closed' | 'due' | 'overdue' | 'paid';

/** `refund` = valor positivo no cadastro/importação; `carryover`/`interest` só pelo pagamento parcial. */
export type CardTransactionKind = 'purchase' | 'refund' | 'carryover' | 'interest' | 'fee';

export interface CreditCardInput {
  name: string;
  brand?: CardBrand | null;
  /** Últimos 4 dígitos. */
  last4?: string | null;
  /** 1..31 — compra no dia >= fechamento cai na fatura seguinte. */
  closing_day: number;
  /** 1..31, ≠ closing_day. */
  due_day: number;
  limit_cents?: number | null;
  /** Conta onde a fatura é paga (gera o lançamento espelho). */
  payment_account_id?: number | null;
  /** Categoria do espelho (movement transfer; padrão "Pagamento de cartão"). */
  category_id?: number | null;
  color?: string | null;
  is_active?: boolean;
}

export interface CreditCard extends CreditCardInput {
  id: number;
  payment_account_name?: string | null;
  category_name?: string | null;
  /** Fatura da competência de hoje (resumida, sem itens). */
  current_invoice?: CreditCardInvoice | null;
  /** Soma das faturas não pagas (negativo = a pagar). */
  open_total_cents: number;
  created_at: string;
  updated_at?: string;
}

export interface CreditCardInvoice {
  id: number;
  credit_card_id: number;
  credit_card_name: string;
  credit_card_color?: string | null;
  year: number;
  month: number;
  /** "Nov/2026" */
  label: string;
  closing_date: string;
  due_date: string;
  status: InvoiceStatus;
  /** Soma dos itens (negativo = a pagar). */
  total_cents: number;
  items_count: number;
  paid_at?: string | null;
  paid_amount_cents?: number | null;
  /** Lançamento espelho na conta de pagamento. */
  payment_transaction_id?: number | null;
  items?: CardTransaction[];
}

export interface CardTransactionInput {
  credit_card_id: number;
  purchase_date: string;
  description: string;
  /** ≠ 0, com sinal (negativo = compra, positivo = estorno). */
  amount_cents: number;
  category_id?: number | null;
  notes?: string | null;
  /** 1..72 (só na criação; padrão 1). */
  installments?: number;
  original_amount_cents?: number | null;
  /** 3 letras (ex.: USD). */
  original_currency?: string | null;
}

export interface CardTransaction {
  id: number;
  credit_card_id: number;
  credit_card_name: string;
  credit_card_invoice_id: number;
  invoice_label: string;
  purchase_date: string;
  description: string;
  amount_cents: number;
  kind: CardTransactionKind;
  category_id?: number | null;
  category_name?: string | null;
  category_color?: string | null;
  notes?: string | null;
  transaction_group?: number | null;
  repeat_index?: number | null;
  repeat_total?: number | null;
  recurring_transaction_id?: number | null;
  original_amount_cents?: number | null;
  original_currency?: string | null;
  fit_id?: string | null;
  import_batch_id?: number | null;
  created_at: string;
  updated_at?: string;
  last_updated_by?: string;
}

export interface CardTransactionFilter {
  credit_card_id?: number;
  invoice_id?: number;
  start?: string;
  end?: string;
  q?: string;
  category_id?: number;
  uncategorized?: boolean;
  page?: number;
  page_size?: number;
}

export interface PayInvoiceInput {
  paid_at: string;
  /** > 0 (valor absoluto pago). Menor que o total → diferença vira `carryover` na fatura seguinte. */
  amount_cents: number;
  /** >= 0 → item `interest` na fatura seguinte. */
  interest_cents?: number;
}

export interface ProjectionCard {
  id: number;
  name: string;
  color?: string | null;
}

/** Linha da matriz: compra parcelada (`transaction_group`) ou recorrência (`key: "rec:<id>"`). */
export interface ProjectionGroup {
  key: string;
  /** Sem o sufixo " i/N". */
  description: string;
  recurring: boolean;
  credit_card_id: number;
  repeat_total?: number | null;
  total_cents: number;
  /** Alinhado a `months`. */
  month_amounts: number[];
  /** "03/12" ou "" — alinhado a `months`. */
  month_labels: string[];
}

/** Itens avulsos (e `carryover`) por cartão e competência. */
export interface ProjectionOther {
  credit_card_id: number;
  month_amounts: number[];
}

export interface CreditCardProjection {
  /** "2026-11", … (mês corrente + months−1). */
  months: string[];
  cards: ProjectionCard[];
  groups: ProjectionGroup[];
  others: ProjectionOther[];
  /** Por mês, todos os cartões. */
  totals: number[];
}

// ---------------------------------------------------------------- recorrências financeiras

export type RecurringTargetType = 'account' | 'card';

export interface RecurringTransactionInput {
  target_type: RecurringTargetType;
  target_id: number;
  /** 1..31 (preso ao fim do mês). */
  transaction_day: number;
  description: string;
  /** ≠ 0, com sinal. É uma estimativa: a linha gerada pode ser editada. */
  amount_cents: number;
  category_id?: number | null;
  notes?: string | null;
  /** AAAAMM */
  start_competence: number;
  /** AAAAMM >= start_competence; omitido = sem fim. */
  end_competence?: number | null;
  is_active?: boolean;
  /** Fase 7: "pago por agendamento no banco" — só `account` e despesa (senão 400). */
  requires_scheduling?: boolean;
  /** 0..30 (padrão 5). */
  schedule_lead_days?: number;
}

export interface RecurringTransaction extends RecurringTransactionInput {
  id: number;
  target_name: string;
  target_color?: string | null;
  category_name?: string | null;
  next_date?: string | null;
  /** Competências (AAAAMM) puladas. */
  skips: number[];
  created_at: string;
  updated_at?: string;
}

// ---------------------------------------------------------------- orçamento por categoria (Fase 5)

/** `competence = 0` é o padrão mensal; AAAAMM é a exceção daquele mês (vence o padrão). */
export interface CategoryBudgetInput {
  competence: number;
  /** > 0 (centavos). */
  amount_cents: number;
}

export type BudgetSource = 'none' | 'default' | 'competence';

/** `warning` ≥ 80 %, `exceeded` > 100 %; `none` sem orçamento. */
export type BudgetStatus = 'none' | 'ok' | 'warning' | 'exceeded';

export interface BudgetItem {
  category_id: number;
  name: string;
  color?: string | null;
  parent_category_id?: number | null;
  /** Orçamento efetivo (exceção do mês ou padrão). */
  budget_cents?: number | null;
  budget_source: BudgetSource;
  default_cents?: number | null;
  competence_cents?: number | null;
  /** Realizado (datas ≤ hoje), positivo; em categoria pai soma as filhas. */
  spent_cents: number;
  /** Total com previstos, positivo. */
  projected_cents: number;
  /** spent / budget × 100 (1 casa). */
  pct?: number | null;
  status: BudgetStatus;
}

export interface BudgetReport {
  year: number;
  month: number;
  total_budget_cents: number;
  total_spent_cents: number;
  total_projected_cents: number;
  /** Uma linha por categoria de despesa ativa (raízes e filhas). */
  items: BudgetItem[];
}

/** Resumo do dashboard ("Hoje"): as 5 orçadas de maior pct. Ausente quando não há orçamento. */
export interface BudgetSummary {
  year: number;
  month: number;
  total_budget_cents: number;
  total_spent_cents: number;
  items: BudgetItem[];
}

// ---------------------------------------------------------------- relatórios (Fase 5)

/** `realized` (padrão) só datas ≤ hoje; `projected` inclui previstos. */
export type ReportBasis = 'realized' | 'projected';

export interface ExpenseCategory {
  /** null = "Sem categoria". */
  category_id?: number | null;
  name: string;
  color?: string | null;
  /** Positivo. */
  amount_cents: number;
  /** Do total do mês, 1 casa. */
  pct: number;
  prev_month_cents: number;
  count: number;
  children?: ExpenseCategory[] | null;
}

export interface ExpenseReport {
  year: number;
  month: number;
  basis: ReportBasis;
  total_cents: number;
  prev_month_total_cents: number;
  same_month_last_year_total_cents: number;
  accounts_cents: number;
  cards_cents: number;
  income_cents: number;
  /** Só raízes (com `children`), por valor desc. */
  categories: ExpenseCategory[];
}

export interface ReportEntry {
  source: 'account' | 'card';
  id: number;
  date: string;
  description: string;
  /** Positivo. */
  amount_cents: number;
  category_id?: number | null;
  category_name?: string | null;
  /** Conta ou cartão. */
  origin_name: string;
  invoice_label?: string | null;
  is_projected: boolean;
}

export interface AnnualCategory {
  category_id?: number | null;
  name: string;
  color?: string | null;
  /** 12 posições, alinhadas a `months`. */
  month_amounts: number[];
  total_cents: number;
}

export interface AnnualReport {
  year: number;
  basis: ReportBasis;
  /** "2026-01" … "2026-12". */
  months: string[];
  /** Raízes (filhas somadas), total desc. */
  categories: AnnualCategory[];
  expense_months: number[];
  income_months: number[];
  net_months: number[];
  total_expense_cents: number;
  total_income_cents: number;
}

export interface CashflowMonth {
  /** "2026-11" */
  ym: string;
  /** "Nov/2026" */
  label: string;
  opening_cents: number;
  income_cents: number;
  /** Negativo. */
  expense_cents: number;
  closing_cents: number;
  realized_income_cents: number;
  realized_expense_cents: number;
  projected_income_cents: number;
  projected_expense_cents: number;
  is_current: boolean;
  is_future: boolean;
}

export interface CashflowReport {
  start: string;
  end: string;
  account_id?: number | null;
  account_name?: string | null;
  months: CashflowMonth[];
}

export const REPORT_BASIS_LABELS: Record<ReportBasis, string> = {
  realized: 'Realizado',
  projected: 'Com previstos',
};

export const BUDGET_STATUS_LABELS: Record<BudgetStatus, string> = {
  none: 'Sem orçamento',
  ok: 'Dentro',
  warning: 'Atenção',
  exceeded: 'Estourado',
};

export const BUDGET_SOURCE_LABELS: Record<BudgetSource, string> = {
  none: '—',
  default: 'padrão',
  competence: 'só este mês',
};

// ---------------------------------------------------------------- rótulos

export const ACCOUNT_TYPES: AccountType[] = ['checking', 'savings', 'investment', 'cash'];

export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  checking: 'Conta corrente',
  savings: 'Poupança',
  investment: 'Investimento',
  cash: 'Dinheiro',
};

export const MOVEMENTS: Movement[] = ['expense', 'income', 'transfer'];

export const MOVEMENT_LABELS: Record<Movement, string> = {
  expense: 'Despesa',
  income: 'Receita',
  transfer: 'Transferência',
};

export const MOVEMENT_LABELS_PLURAL: Record<Movement, string> = {
  expense: 'Despesas',
  income: 'Receitas',
  transfer: 'Transferências',
};

export const TRANSACTION_KIND_LABELS: Record<TransactionKind, string> = {
  all: 'Todos',
  expense: 'Despesas',
  income: 'Receitas',
  transfer: 'Transferências',
};

export const IMPORT_BATCH_STATUS_LABELS: Record<ImportBatchStatus, string> = {
  previewing: 'Em prévia',
  confirmed: 'Importado',
  discarded: 'Descartado',
};

export const IMPORT_LINE_STATUS_LABELS: Record<ImportLineStatus, string> = {
  new: 'Nova',
  existing: 'Já existe',
  ignored: 'Ignorada',
  imported: 'Importada',
};

export const IMPORT_TARGET_TYPE_LABELS: Record<ImportTargetType, string> = {
  account: 'Conta',
  card: 'Cartão',
};

export const IMPORT_RULE_APPLIES_TO_LABELS: Record<ImportRuleAppliesTo, string> = {
  both: 'Contas e cartões',
  account: 'Só contas',
  card: 'Só cartões',
};

export const IMPORT_FILE_FORMAT_LABELS: Record<ImportFileFormat, string> = {
  ofx: 'OFX',
  csv: 'CSV',
  xlsx: 'XLSX',
  pdf: 'PDF',
};

/** Formatos de data do contrato (notação Go) e como o usuário os vê. */
export const IMPORT_DATE_FORMATS: { value: ImportDateFormat; label: string }[] = [
  { value: '02/01/2006', label: 'dd/MM/aaaa' },
  { value: '2006-01-02', label: 'aaaa-MM-dd' },
  { value: '02-01-2006', label: 'dd-MM-aaaa' },
  { value: '2/1/2006', label: 'd/M/aaaa' },
];

export const IMPORT_DELIMITERS: { value: string; label: string }[] = [
  { value: ';', label: 'Ponto e vírgula (;)' },
  { value: ',', label: 'Vírgula (,)' },
  { value: '\t', label: 'Tabulação' },
  { value: '|', label: 'Barra vertical (|)' },
];

/** Limite do arquivo de importação (contrato: 5 MB). */
export const IMPORT_MAX_BYTES = 5 * 1024 * 1024;

export const IMPORT_ACCEPT = '.ofx,.csv,.xlsx,.pdf';

/** Extensões aceitas por alvo: PDF vale para fatura de cartão e para extrato de conta (app do Itaú). */
export const importAcceptFor = (_target: ImportTargetType): string => IMPORT_ACCEPT;

/** Payload completo de PUT /accounts/:id a partir de uma conta existente (ex.: reativar). */
export const accountToInput = (a: Account, overrides: Partial<AccountInput> = {}): AccountInput => ({
  name: a.name,
  account_type: a.account_type,
  bank_code: a.bank_code ?? null,
  bank_name: a.bank_name ?? null,
  branch: a.branch ?? null,
  number: a.number ?? null,
  color: a.color ?? null,
  is_active: a.is_active ?? true,
  ...overrides,
});

/** Movimento de um lançamento pelo sinal (transferência pela perna). */
export const movementOf = (t: Pick<Transaction, 'amount_cents' | 'transfer_id'>): Movement =>
  t.transfer_id ? 'transfer' : t.amount_cents < 0 ? 'expense' : 'income';

/** Formato do arquivo pela extensão (a detecção definitiva é do backend, por conteúdo). */
export const fileFormatFromName = (name: string): ImportFileFormat | null => {
  const ext = name.toLowerCase().split('.').pop();
  if (ext === 'ofx') return 'ofx';
  if (ext === 'csv' || ext === 'txt') return 'csv';
  if (ext === 'xlsx') return 'xlsx';
  if (ext === 'pdf') return 'pdf';
  return null;
};

export const CARD_BRANDS: CardBrand[] = ['visa', 'mastercard', 'elo', 'amex', 'hipercard', 'outro'];

export const CARD_BRAND_LABELS: Record<CardBrand, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  elo: 'Elo',
  amex: 'American Express',
  hipercard: 'Hipercard',
  outro: 'Outra',
};

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  open: 'Aberta',
  closed: 'Fechada',
  due: 'Vence hoje',
  overdue: 'Vencida',
  paid: 'Paga',
};

export const CARD_TRANSACTION_KIND_LABELS: Record<CardTransactionKind, string> = {
  purchase: 'Compra',
  refund: 'Estorno',
  carryover: 'Saldo anterior',
  interest: 'Juros e encargos',
  fee: 'Tarifa',
};

/** Fase 7: dias de aviso antes do vencimento (contrato: 0..30, padrão 5). */
export const DEFAULT_SCHEDULE_LEAD_DAYS = 5;
export const MAX_SCHEDULE_LEAD_DAYS = 30;

/** Limita os dias de aviso ao contrato (0..30); vazio/inválido → padrão 5. */
export const clampLeadDays = (raw: number | string | null | undefined): number => {
  const n = typeof raw === 'number' ? raw : Number(String(raw ?? '').trim() === '' ? NaN : raw);
  if (!Number.isFinite(n)) return DEFAULT_SCHEDULE_LEAD_DAYS;
  return Math.min(MAX_SCHEDULE_LEAD_DAYS, Math.max(0, Math.round(n)));
};

export const SCHEDULE_STATUS_LABELS: Record<ScheduleStatus, string> = {
  none: '',
  to_schedule: 'A agendar',
  upcoming: 'Aguardando',
  scheduled: 'Agendada',
  missed: 'Venceu sem agendar',
};

/** Opções do filtro "Agendamento" de Lançamentos ('' = todos). */
export const SCHEDULE_FILTER_OPTIONS: { value: ScheduleStatusFilter | ''; label: string }[] = [
  { value: '', label: 'Todos' },
  { value: 'pending', label: 'Pendentes' },
  { value: 'to_schedule', label: 'A agendar' },
  { value: 'scheduled', label: 'Agendadas' },
  { value: 'missed', label: 'Venceu sem agendar' },
];

/**
 * `?agendamento=` de Lançamentos (link do e-mail e do painel): `pendente(s)` → `pending`; aceita também
 * os valores do contrato (`pending`, `to_schedule`, `upcoming`, `scheduled`, `missed`). Inválido → ''.
 */
export const scheduleFilterFromParam = (raw?: string | null): ScheduleStatusFilter | '' => {
  const v = (raw ?? '').trim().toLowerCase();
  if (v === 'pendente' || v === 'pendentes') return 'pending';
  const valid: ScheduleStatusFilter[] = ['pending', 'to_schedule', 'upcoming', 'scheduled', 'missed'];
  return (valid as string[]).includes(v) ? (v as ScheduleStatusFilter) : '';
};

/** Marcar/desmarcar agendamento na linha: despesa comum que exige agendamento ou já está agendada. */
export const canToggleSchedule = (
  t: Pick<Transaction, 'amount_cents' | 'requires_scheduling' | 'scheduled_at' | 'credit_card_invoice_id' | 'transfer_id'>,
): boolean =>
  t.amount_cents < 0 && !t.credit_card_invoice_id && !t.transfer_id && (!!t.requires_scheduling || !!t.scheduled_at);

export const RECURRING_TARGET_TYPE_LABELS: Record<RecurringTargetType, string> = {
  account: 'Conta',
  card: 'Cartão',
};

/** Payload completo de PUT /credit-cards/:id a partir de um cartão existente (ex.: reativar). */
export const creditCardToInput = (c: CreditCard, overrides: Partial<CreditCardInput> = {}): CreditCardInput => ({
  name: c.name,
  brand: c.brand ?? null,
  last4: c.last4 ?? null,
  closing_day: c.closing_day,
  due_day: c.due_day,
  limit_cents: c.limit_cents ?? null,
  payment_account_id: c.payment_account_id ?? null,
  category_id: c.category_id ?? null,
  color: c.color ?? null,
  is_active: c.is_active ?? true,
  ...overrides,
});

/** Payload completo de PUT /recurring-transactions/:id a partir de uma recorrência (ex.: reativar). */
export const recurringToInput = (r: RecurringTransaction, overrides: Partial<RecurringTransactionInput> = {}): RecurringTransactionInput => ({
  target_type: r.target_type,
  target_id: r.target_id,
  transaction_day: r.transaction_day,
  description: r.description,
  amount_cents: r.amount_cents,
  category_id: r.category_id ?? null,
  notes: r.notes ?? null,
  start_competence: r.start_competence,
  end_competence: r.end_competence ?? null,
  is_active: r.is_active ?? true,
  requires_scheduling: r.requires_scheduling ?? false,
  schedule_lead_days: r.schedule_lead_days ?? DEFAULT_SCHEDULE_LEAD_DAYS,
  ...overrides,
});

/** Item de fatura gerado pelo pagamento parcial: não se edita nem exclui (desfaça o pagamento). */
export const isPaymentGeneratedItem = (t: Pick<CardTransaction, 'kind'>): boolean => t.kind === 'carryover' || t.kind === 'interest';
